import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useSignalR } from 'src/hooks/useSignalR'
// ========================================
// PRIVATE STORAGE KEYS
// ========================================
// These keys are private to the auth store to prevent direct storage access
// Always use the store's methods instead of accessing storage directly
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'token', // sessionStorage
  REFRESH_TOKEN: 'refreshToken', // localStorage
  SAS_TOKEN: 'sasToken', // sessionStorage (legacy)
  LAST_SESSION: 'lastSessionInfo', // localStorage
  TENANT_CONTEXT: 'tenantContext', // sessionStorage (tenant state)
} as const

// ========================================
// USER OBJECT CONSTRUCTION
// ========================================

// Base user type (common fields)
export interface BaseUser extends IBasePerson {
  sub: string
  id: string
  email: string
  role: Roles
  exp: number
  iat: number
  type: 'admin' | 'client'
}

// Client user type (extends base)
export interface ClientUser extends BaseUser {
  familyId: string
  memberId?: string
  passwordUpdated: boolean
}

// Admin user type (extends base)
export type AdminUser = BaseUser

import { jwtDecode } from 'jwt-decode'
import { Roles } from 'src/common/types'
import type { IBasePerson } from 'src/interfaces/common'
import { usePushNotifications } from 'src/hooks/usePushNotifications'
import * as Sentry from '@sentry/vue'

// ========================================
// MODULE-LEVEL STATE
// ========================================
// Store timeout ID at module level to survive store recreation
let refreshTimeoutId: ReturnType<typeof setTimeout> | undefined

// ========================================
// INTERFACES
// ========================================

export interface BaseTokenPayload {
  // Standard JWT claims
  aud: string // Audience (application ID)
  iss: string // Issuer (Azure B2C issuer URL)
  exp: number // Expiration timestamp
  nbf: number // Not before timestamp
  iat: number // Issued at timestamp
  sub: string // Subject (user ID)
  ver: string // Token version
  azp: string // Authorized party (application ID)
  tfp: string // Trust Framework Policy (B2C flow)

  // User identity
  oid: string // Object ID (Azure AD user ID)
  idp: string // Identity provider (e.g., "LocalAccount")
  given_name: string // First name
  family_name: string // Last name
  name: string // Full name (display name)
  emails: string[] // Email addresses

  // Custom extension attributes (base)
  extension_Role: Roles // User role
}

/**
 * Client/Parent portal JWT token payload
 * Includes org, center, family and member-specific context
 */
export interface ClientTokenPayload extends BaseTokenPayload {
  // Organizational context
  extension_CenterId: string // Center ID
  extension_OrgId: string // Organization ID

  // Client-specific extension attributes
  extension_FamilyId: string // Family ID
  extension_Id?: string // Member ID
  extension_PasswordUpdated: boolean // Password updated flag
  extension_UserEmail: string // Plain text email
}

/**
 * Admin portal JWT token payload
 * Context varies by role:
 * - SuperAdmin: No org/center (access to all)
 * - OrgAdmin: Only orgId (access to all centers in org)
 * - CenterAdmin/OfficeStaff/RoomStaff: Both orgId and centerId
 */
export interface AdminTokenPayload extends BaseTokenPayload {
  // Optional organizational context (depends on role)
  extension_CenterId?: string // Center ID (may contain multiple IDs separated by ';')
  extension_OrgId?: string // Organization ID
}

// Track last session info for universal auth context
export interface LastSessionInfo {
  lastLoginMode?: 'admin' | 'client'
  lastRefreshIssuedAt?: number // unix timestamp (seconds)
  lastOrgId?: string
  lastCenterId?: string
  lastSub?: string // User subject ID from JWT - used to detect user changes
}

// ========================================
// TYPE GUARDS (for consumer use)
// ========================================

/**
 * Check if token payload is for a client user
 * Cast the payload to ClientTokenPayload after this check
 */
export function isClientToken(
  payload?: BaseTokenPayload,
): payload is ClientTokenPayload {
  if (!payload) return false
  return 'extension_FamilyId' in payload
}

/**
 * Check if token payload is for an admin user
 * Cast the payload to AdminTokenPayload after this check
 */
export function isAdminToken(
  payload?: BaseTokenPayload,
): payload is AdminTokenPayload {
  if (!payload) return false
  return !('extension_FamilyId' in payload)
}

// ========================================
// STORE DEFINITION
// ========================================
export const useAuthStore = defineStore('common.auth', () => {
  // ========================================
  // STATE
  // ========================================
  const accessToken = ref<string>()
  const refreshToken = ref<string>()
  const legacyBlobSasToken = ref<string>() // V1 blob storage SAS token (legacy)
  const tokenPayload = ref<BaseTokenPayload>()

  const lastSessionInfo = ref<LastSessionInfo>({})

  // Tenant-related state (merged from tenant.store)
  const currentOrgId = ref<string>()
  const currentCenterId = ref<string>()
  const availableCenters = ref<string[]>()

  // ========================================
  // COMPUTED
  // ========================================

  /**
   * Construct a unified user object from tokenPayload
   * Returns undefined if not authenticated
   *
   * NOTE: This is a PURE computed - no side effects/mutations allowed
   * Tenant store updates are handled in setAccessToken() instead
   */
  const user = computed<BaseUser | undefined>(() => {
    const payload = tokenPayload.value
    if (!payload) return undefined

    // Client user
    if (isClientToken(payload)) {
      const clientUser: ClientUser = {
        sub: payload.sub,
        id: payload.extension_Id ?? payload.sub,
        preferredName: payload.name,
        firstName: payload.given_name || '',
        lastName: payload.family_name || '',
        email: payload.extension_UserEmail || '',
        role: payload.extension_Role,
        familyId: payload.extension_FamilyId,
        memberId: payload.extension_Id,
        passwordUpdated: payload.extension_PasswordUpdated,
        exp: payload.exp,
        iat: payload.iat,
        type: 'client',
      }
      return clientUser
    }

    // Admin user
    if (isAdminToken(payload)) {
      const adminUser: AdminUser = {
        sub: payload.sub,
        id: payload.sub,
        preferredName: payload.name,
        firstName: payload.given_name,
        lastName: payload.family_name,
        email: payload.emails?.[0] || '',
        role: payload.extension_Role,
        exp: payload.exp,
        iat: payload.iat,
        type: 'admin',
      }
      return adminUser
    }

    return undefined
  })
  const isSuperAdmin = computed(() => role.value === Roles.SuperAdmin)
  const isOrgAdmin = computed(() => role.value === Roles.OrgAdmin)
  const isCenterAdmin = computed(() => role.value === Roles.CenterAdmin)
  const isOfficeStaff = computed(() => role.value === Roles.OfficeStaff)
  const isRoomStaff = computed(() => role.value === Roles.RoomStaff)
  const isClient = computed(() => role.value === Roles.Client)
  const isAdmin = computed(
    () =>
      role.value === Roles.SuperAdmin ||
      role.value === Roles.OrgAdmin ||
      role.value === Roles.CenterAdmin ||
      role.value === Roles.OfficeStaff,
  )
  const userEmail = computed(() => {
    if (role.value === Roles.Client)
      return (
        (tokenPayload.value as ClientTokenPayload)?.extension_UserEmail || ''
      )

    return tokenPayload.value?.emails?.[0] || ''
  })
  /**
   * Check if user is authenticated (has valid access token)
   */
  const isAuthenticated = computed(() => {
    return !!accessToken.value && !isAccessTokenExpired.value
  })

  /**
   * Check if access token is expired
   */
  const isAccessTokenExpired = computed(() => {
    if (!accessToken.value) return true

    try {
      const payload = decodeToken(accessToken.value)
      if (!payload.exp) return false // No expiration means token doesn't expire

      const now = Math.floor(Date.now() / 1000)
      // Consider expired if less than 60 seconds remaining
      return payload.exp < now + 60
    } catch {
      return true
    }
  })

  /**
   * Check if user must change password (admin set temp password)
   * Derived from token payload's extension_PasswordUpdated field
   * false = admin set temp password, user must change it
   * Only applicable to client tokens - admin tokens don't have this field
   */
  const requiresPasswordChange = computed(() => {
    if (!tokenPayload.value) return false

    // Only client tokens have the extension_PasswordUpdated field
    // Check by checking if the field exists
    const clientPayload = tokenPayload.value as ClientTokenPayload
    if ('extension_PasswordUpdated' in clientPayload) {
      return clientPayload.extension_PasswordUpdated === true ? false : true
    }

    // Admin tokens don't require password change check
    return false
  })

  /**
   * Get user role from token payload
   */
  const role = computed(
    () => tokenPayload.value?.extension_Role ?? Roles.Anonymous,
  )
  // ========================================
  // HELPER FUNCTIONS (must be declared before computed properties)
  // ========================================

  /**
   * Decode JWT token (does not verify signature)
   * Returns base token payload - cast to ClientTokenPayload or AdminTokenPayload based on role
   */
  const decodeToken = (token: string): BaseTokenPayload => {
    return jwtDecode<BaseTokenPayload>(token)
  }

  /**
   * Persist current state to storage
   */
  const persist = () => {
    try {
      // Access token -> session storage
      if (accessToken.value) {
        sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken.value)
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
      }

      // Refresh token -> local storage
      if (refreshToken.value) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken.value)
      } else {
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
      }

      // Legacy SAS token -> session storage
      if (legacyBlobSasToken.value) {
        sessionStorage.setItem(STORAGE_KEYS.SAS_TOKEN, legacyBlobSasToken.value)
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.SAS_TOKEN)
      }

      // Last session info -> local storage
      if (lastSessionInfo.value) {
        localStorage.setItem(
          STORAGE_KEYS.LAST_SESSION,
          JSON.stringify(lastSessionInfo.value),
        )
      }

      // Tenant context -> session storage
      const tenantContext = {
        currentOrgId: currentOrgId.value,
        currentCenterId: currentCenterId.value,
        availableCenters: availableCenters.value,
      }
      sessionStorage.setItem(
        STORAGE_KEYS.TENANT_CONTEXT,
        JSON.stringify(tenantContext),
      )
    } catch {
      // Silent fail
    }
  }

  /**
   * Cancel any scheduled token refresh
   */
  const cancelScheduledRefresh = (): void => {
    if (refreshTimeoutId) {
      clearTimeout(refreshTimeoutId)
      refreshTimeoutId = undefined
    }
  }

  /**
   * Schedule automatic token refresh based on JWT expiration
   * Refreshes 5 minutes before token expires
   */
  const scheduleTokenRefresh = (): void => {
    // Cancel any existing scheduled refresh
    cancelScheduledRefresh()

    // Get token payload
    const payload = tokenPayload.value
    if (!payload?.exp) {
      return
    }

    // Calculate when to refresh (5 minutes before expiration)
    const now = Math.floor(Date.now() / 1000)
    const expiresIn = payload.exp - now
    const refreshIn = Math.max(expiresIn - 300, 0) // 300 seconds = 5 minutes buffer

    if (refreshIn <= 0) {
      void performTokenRefresh()
      return
    }

    // Schedule the refresh
    refreshTimeoutId = setTimeout(() => {
      void performTokenRefresh()
    }, refreshIn * 1000)
  }

  /**
   * Perform the actual token refresh
   * Uses different API endpoints based on user role:
   * - Client: /session/clientToken (client portal)
   * - Admin: /session/token (admin portal)
   */
  const performTokenRefresh = async (): Promise<void> => {
    if (!refreshToken.value) {
      return
    }

    try {
      // Determine which refresh token API to use based on role
      // At this time access token is not resolved. so role is unavailable
      const { refreshToken: refreshTokenApi } = await import(
        lastSessionInfo.value.lastLoginMode === 'client'
          ? 'src/client/services/auth.client'
          : 'src/service/auth'
      )
      const response = await refreshTokenApi({
        refreshToken: refreshToken.value,
      })

      // Update lastSessionInfo for refresh
      lastSessionInfo.value.lastRefreshIssuedAt = Math.floor(Date.now() / 1000)
      persist()

      // Update authStore (primary) - this will auto-schedule next refresh
      await setTokens(response.data.access_token, response.data.refresh_token)

      if (response.data.sas_token) {
        setLegacyBlobSasToken(response.data.sas_token)
      }
    } catch (error) {
      Sentry.captureException(error)
      // Clear invalid refresh token to prevent infinite retry loops
      // This handles cases where user logged out server-side or token expired
      refreshToken.value = undefined
      tokenPayload.value = undefined
      accessToken.value = undefined
      persist()
      // Can't directly use router from a hook
      void import('src/router').then(({ router }) => {
        void router.push({ name: 'welcome' })
      })
    }
  }

  // ========================================
  // TENANT ACTIONS (merged from tenant.store)
  // ========================================

  /**
   * Set current organization ID and update lastSessionInfo
   */
  const setCurrentOrgId = (orgId?: string) => {
    currentOrgId.value = orgId
    if (orgId) {
      lastSessionInfo.value.lastOrgId = orgId
    }
    persist()
  }

  /**
   * Set current center ID and update lastSessionInfo
   */
  const setCurrentCenterId = (centerId?: string) => {
    currentCenterId.value = centerId
    if (centerId) {
      lastSessionInfo.value.lastCenterId = centerId
    }
    persist()
  }

  /**
   * Set available centers (for admin users with multiple center access)
   */
  const setAvailableCenters = (centers?: string[]) => {
    availableCenters.value = centers
    persist()
  }

  /**
   * Clear tenant context (org/center/availableCenters)
   */
  const clearTenantContext = () => {
    currentOrgId.value = undefined
    currentCenterId.value = undefined
    availableCenters.value = undefined
    persist()
  }

  // ========================================
  // AUTH ACTIONS
  // ========================================

  /**
   * Set access token and persist to session storage
   * Automatically decodes token and schedules token refresh based on expiration
   */
  const setAccessToken = async (
    token?: string,
    loginMode?: 'admin' | 'client',
    orgId?: string,
    centerId?: string,
  ): Promise<void> => {
    accessToken.value = token

    // Decode and store token payload (mustChangePassword will auto-compute from payload)
    if (token) {
      try {
        tokenPayload.value = decodeToken(token)

        // Update tenant store and lastSessionInfo based on token payload
        // (moved from user computed to avoid side effects in computed properties)
        const payload = tokenPayload.value
        if (payload) {
          // Client token
          if (isClientToken(payload)) {
            setCurrentOrgId(payload.extension_OrgId)
            setCurrentCenterId(payload.extension_CenterId)
            lastSessionInfo.value.lastSub = payload.sub
          }

          // Admin token
          if (isAdminToken(payload)) {
            let orgChanged = false
            if (
              payload.extension_OrgId &&
              currentOrgId.value !== payload.extension_OrgId
            ) {
              setCurrentOrgId(payload.extension_OrgId)
              orgChanged = true
            }

            // extension_CenterId may be a semicolon-separated list
            // Center id may be set on org admin users.
            const fullCenterAccessRoles = [Roles.SuperAdmin, Roles.OrgAdmin]
            if (
              payload.extension_CenterId &&
              !fullCenterAccessRoles.includes(payload.extension_Role)
            ) {
              const centers = payload.extension_CenterId
                .split(';')
                .filter(Boolean)

              if (centers.length > 0) {
                // Always update available centers (permissions may have changed)
                setAvailableCenters(centers)

                // Reset center if:
                // 1. Org changed (different org context)
                // 2. No center is currently set (initial login)
                // 3. Current center is no longer in the available list (access revoked)
                if (
                  orgChanged ||
                  !currentCenterId.value ||
                  !centers.includes(currentCenterId.value)
                ) {
                  setCurrentCenterId(centers[0])
                }
              }
            }

            lastSessionInfo.value.lastSub = payload.sub
          }
        }
      } catch {
        tokenPayload.value = undefined
      }
    } else {
      tokenPayload.value = undefined
    }

    // If loginMode is provided, update lastSessionInfo
    if (loginMode) {
      lastSessionInfo.value.lastLoginMode = loginMode
      lastSessionInfo.value.lastRefreshIssuedAt = Math.floor(Date.now() / 1000)
      if (orgId) lastSessionInfo.value.lastOrgId = orgId
      if (centerId) lastSessionInfo.value.lastCenterId = centerId
    }

    persist()
    scheduleTokenRefresh() // Auto-schedule refresh when token is set

    // Register for push notifications after successful login
    if (token && isAuthenticated.value) {
      await usePushNotifications().registerWithBackend()
    }
  }

  /**
   * Set refresh token and persist to local storage
   */
  const setRefreshToken = (token?: string): void => {
    refreshToken.value = token
    persist()
  }

  /**
   * Set legacy blob SAS token and persist to session storage
   * @deprecated This is for V1/legacy blob storage URLs only
   */
  const setLegacyBlobSasToken = (token?: string): void => {
    legacyBlobSasToken.value = token
    persist()
  }

  /**
   * Set both tokens at once (e.g., after login)
   */
  // Optionally pass loginMode/orgId/centerId for tracking
  const setTokens = async (
    access: string,
    refresh: string,
    loginMode?: 'admin' | 'client',
    orgId?: string,
    centerId?: string,
  ): Promise<void> => {
    await setAccessToken(access, loginMode, orgId, centerId)
    setRefreshToken(refresh)
  }

  /**
   * Clear all tokens and remove from storage (logout)
   * Also cancels any scheduled token refresh
   */
  const logout = async (): Promise<void> => {
    await usePushNotifications().unregisterFromBackend()
    cancelScheduledRefresh() // Cancel scheduled refresh on logout
    await useSignalR().stopConnections()
    accessToken.value = undefined
    refreshToken.value = undefined
    legacyBlobSasToken.value = undefined
    tokenPayload.value = undefined // mustChangePassword will auto-reset via computed
    persist()
  }

  const updateLastSessionInfo = (info: Partial<LastSessionInfo>) => {
    lastSessionInfo.value = { ...lastSessionInfo.value, ...info }
    persist()
  }

  /**
   * Load tokens from storage and hydrate refs
   * Call this during app boot/initialization
   * NOTE: Does NOT auto-schedule token refresh - call scheduleTokenRefresh() manually
   * when entering client mode to avoid scheduling refresh for admin users
   */
  const loadFromStorage = async (): Promise<void> => {
    try {
      const storedAccessToken = sessionStorage.getItem(
        STORAGE_KEYS.ACCESS_TOKEN,
      )
      const storedRefreshToken = localStorage.getItem(
        STORAGE_KEYS.REFRESH_TOKEN,
      )
      const storedSasToken = sessionStorage.getItem(STORAGE_KEYS.SAS_TOKEN)
      const storedSessionInfo = localStorage.getItem(STORAGE_KEYS.LAST_SESSION)

      // If we have an access token but no refresh token, force logout
      // This indicates user logged out or session was cleared
      if (storedAccessToken && !storedRefreshToken) {
        accessToken.value = undefined
        tokenPayload.value = undefined
        legacyBlobSasToken.value = undefined
        clearTenantContext()
        return
      }

      if (storedAccessToken) {
        accessToken.value = storedAccessToken
        // Decode and store token payload (mustChangePassword will auto-compute from payload)
        try {
          tokenPayload.value = decodeToken(storedAccessToken)

          // Restore tenant context from token payload
          const payload = tokenPayload.value
          if (payload) {
            if (isClientToken(payload)) {
              setCurrentOrgId(payload.extension_OrgId)
              setCurrentCenterId(payload.extension_CenterId)
            }

            if (isAdminToken(payload)) {
              if (payload.extension_OrgId) {
                setCurrentOrgId(payload.extension_OrgId)
              }

              // Restore availableCenters from token
              const fullCenterAccessRoles = [Roles.SuperAdmin, Roles.OrgAdmin]
              if (
                payload.extension_CenterId &&
                !fullCenterAccessRoles.includes(payload.extension_Role)
              ) {
                const centers = payload.extension_CenterId
                  .split(';')
                  .filter(Boolean)

                if (centers.length > 0) {
                  // Always update available centers
                  setAvailableCenters(centers)

                  // Only set center if not already set or not in available list
                  if (
                    !currentCenterId.value ||
                    !centers.includes(currentCenterId.value)
                  ) {
                    setCurrentCenterId(centers[0])
                  }
                }
              }
            }
          }
        } catch {
          tokenPayload.value = undefined
        }
      }

      if (storedRefreshToken) refreshToken.value = storedRefreshToken
      if (storedSasToken) legacyBlobSasToken.value = storedSasToken
      if (storedSessionInfo) {
        try {
          lastSessionInfo.value = JSON.parse(storedSessionInfo)
          setCurrentOrgId(lastSessionInfo.value.lastOrgId)
          setCurrentCenterId(lastSessionInfo.value.lastCenterId)
        } catch {
          lastSessionInfo.value = {}
        }
      }

      // Load tenant context from session storage
      const storedTenantContext = sessionStorage.getItem(
        STORAGE_KEYS.TENANT_CONTEXT,
      )
      if (storedTenantContext) {
        try {
          const context = JSON.parse(storedTenantContext)
          // Don't use setters here to avoid triggering persist() multiple times
          currentOrgId.value = context.currentOrgId
          currentCenterId.value = context.currentCenterId
          availableCenters.value = context.availableCenters
        } catch {
          // Silent fail
        }
      }

      // If we don't have an access token but we have a refresh token,
      // immediately attempt to refresh so the app can hydrate client-context
      // (orgId/centerId) from the token payload and allow client hooks to run.
      if (!accessToken.value && refreshToken.value) {
        try {
          // performTokenRefresh will set accessToken and tokenPayload
          await performTokenRefresh()

          // After successful refresh, tokenPayload will be populated.
          // Validate that the user hasn't changed (sub matches)
          if (tokenPayload.value && lastSessionInfo.value.lastSub) {
            if (tokenPayload.value.sub !== lastSessionInfo.value.lastSub) {
              // Different user logged in - invalidate persisted context
              clearTenantContext()
              lastSessionInfo.value = {
                lastSub: tokenPayload.value.sub,
              }
              persist()
            }
          }

          // The auth store's responsibility ends at refreshing tokens and
          // updating token payload. Client context hydration (org/center aliases
          // and orgInfoCenters) is handled by `client.context.store` which
          // persists and rehydrates its own data from localStorage.
        } catch {
          // Clear invalid refresh token to prevent infinite retry loops
          refreshToken.value = undefined
          tokenPayload.value = undefined
          persist()
        }
      }

      // Auto-schedule token refresh if we have a valid token
      // This happens after initial load or after refresh token exchange
      if (accessToken.value && tokenPayload.value) {
        scheduleTokenRefresh()
      }
    } catch {
      // Silent fail
    }
  }

  const overrideExtensionId = (newMemberId: string): void => {
    if (!tokenPayload.value || !isClientToken(tokenPayload.value)) return

    const clientPayload = tokenPayload.value
    clientPayload.extension_Id = newMemberId
    tokenPayload.value = { ...clientPayload }
  }

  // ========================================
  // RETURN PUBLIC API
  // ========================================
  return {
    // Auth State
    accessToken,
    refreshToken,
    legacyBlobSasToken,
    lastSessionInfo,
    tokenPayload,

    // Tenant State (merged from tenant.store)
    currentOrgId,
    currentCenterId,
    availableCenters,

    // Computed
    user,
    userEmail,
    isAuthenticated,
    requiresPasswordChange,
    role,
    isSuperAdmin,
    isOrgAdmin,
    isCenterAdmin,
    isOfficeStaff,
    isRoomStaff,
    isClient,
    isAdmin,

    // Auth Actions
    setLegacyBlobSasToken,
    setTokens,
    logout,
    updateLastSessionInfo,
    loadFromStorage,

    // Tenant Actions (merged from tenant.store)
    setCurrentOrgId,
    setCurrentCenterId,
    setAvailableCenters,
    clearContext: clearTenantContext,
    overrideExtensionId,
  }
})
